import * as React from 'react';

import { Selector as AddressSelector } from '@admin/components/address/selector';
import { Address } from '@admin/types/address';

interface IMovingOperation {
  origin_address?: Address;
  destination_address?: Address;
}

interface IOrder {
  moving_operation: IMovingOperation;
}

interface IAccount {
  id: string;
}

interface IGeocodeResult {
  potentially_invalid: boolean;
}

interface IMovingAddressSelectorsProps {
  account: IAccount;
  order: IOrder;
}

/**
 * Component for selecting origin and destination addresses for a moving order.
 *
 * @param props.account - The account object containing account information
 * @param props.order - The order object containing moving operation addresses
 */
const MovingAddressSelectors: React.FC<IMovingAddressSelectorsProps> = ({ account, order }) => {
  const [originPotentiallyInvalid, setOriginPotentiallyInvalid] = React.useState(false);
  const [destinationPotentiallyInvalid, setDestinationPotentiallyInvalid] = React.useState(false);
  const [originAddress, setOriginAddress] = React.useState<Address | undefined>(order.moving_operation.origin_address);
  const [destinationAddress, setDestinationAddress] = React.useState<Address | undefined>(
    order.moving_operation.destination_address,
  );

  const showAccountAddresses = true;
  const warehouseReturn = false;

  const originAddressSelected = React.useCallback(
    (address: Address) => {
      order.moving_operation.origin_address = address;
      setOriginAddress(address);
    },
    [order],
  );

  const destinationAddressSelected = React.useCallback(
    (address: Address) => {
      order.moving_operation.destination_address = address;
      setDestinationAddress(address);
    },
    [order],
  );

  const originGeocoded = React.useCallback((geocode: IGeocodeResult) => {
    setOriginPotentiallyInvalid(geocode.potentially_invalid);
  }, []);

  const destinationGeocoded = React.useCallback((geocode: IGeocodeResult) => {
    setDestinationPotentiallyInvalid(geocode.potentially_invalid);
  }, []);

  // No-op for warehouse select since moving orders don't use warehouse returns
  const onWarehouseSelect = React.useCallback(() => {}, []);

  return (
    <>
      <h3 className="text-thin">Origin Address</h3>
      <AddressSelector
        selection={originAddress}
        account={account}
        showAccountAddresses={showAccountAddresses}
        onSelect={originAddressSelected}
        potentiallyInvalid={originPotentiallyInvalid}
        warehouseReturn={warehouseReturn}
        onWarehouseSelect={onWarehouseSelect}
        onGeocode={originGeocoded}
      />

      <h3 className="text-thin">Destination Address</h3>
      <AddressSelector
        selection={destinationAddress}
        account={account}
        showAccountAddresses={showAccountAddresses}
        onSelect={destinationAddressSelected}
        potentiallyInvalid={destinationPotentiallyInvalid}
        warehouseReturn={warehouseReturn}
        onWarehouseSelect={onWarehouseSelect}
        onGeocode={destinationGeocoded}
      />
    </>
  );
};

export { MovingAddressSelectors };
